import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import type { CameraSource } from './cameraSource';

interface LocalDevice {
  deviceId: string;
  label: string;
}

interface LocalCameraSource extends CameraSource {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** The device the last live stream came from; undefined until one is live. */
  deviceId: string | undefined;
  devices: LocalDevice[];
}

/**
 * The device's own cameras through `getUserMedia`, live while `active`. Without a
 * `deviceId` it opens the rear camera.
 */
export function useLocalCamera(active: boolean, deviceId?: string): LocalCameraSource {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [liveDeviceId, setLiveDeviceId] = useState<string>();
  const [error, setError] = useState('');
  const [devices, setDevices] = useState<LocalDevice[]>([]);

  useEffect(() => {
    // The previous stream is stopped by now, so the shutter must wait for the new one.
    setStream(null);
    if (!active) {
      setLiveDeviceId(undefined);
      return;
    }
    let live: MediaStream | null = null;
    let cancelled = false;
    const source = deviceId ? { deviceId: { exact: deviceId } } : { facingMode: 'environment' };
    navigator.mediaDevices
      .getUserMedia({
        video: { ...source, width: { ideal: 4096 }, height: { ideal: 4096 } },
        audio: false,
      })
      .then(async (s) => {
        if (cancelled) return stopStream(s);
        live = s;
        // Without ImageCapture the still is the preview frame, so the preview must be full size.
        const track = s.getVideoTracks()[0];
        if (!imageCaptureCtor()) await maximizeResolution(track);
        if (cancelled) return;
        // Unplugging the camera or the OS taking it ends the track without stopping the stream.
        track?.addEventListener('ended', () => {
          if (cancelled) return;
          setStream(null);
          setError('Camera disconnected. Choose from the gallery instead.');
        });
        setStream(s);
        setLiveDeviceId(track?.getSettings().deviceId);
        setError('');
        // Device ids are blank until permission is granted, so list cameras only now.
        const all = await navigator.mediaDevices.enumerateDevices();
        setDevices(
          all
            .filter((d) => d.kind === 'videoinput' && d.deviceId)
            .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` })),
        );
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(cameraError(e));
      });
    return () => {
      cancelled = true;
      if (live) stopStream(live);
    };
  }, [active, deviceId]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream;
  }, [stream]);

  const capture = useCallback(async () => {
    const video = videoRef.current;
    if (!stream || !video) throw new Error('Could not take photo');
    return grabFrame(stream, video);
  }, [stream]);

  return {
    videoRef,
    ready: stream !== null,
    error,
    capture,
    deviceId: liveDeviceId,
    devices,
  };
}

function stopStream(stream: MediaStream) {
  for (const track of stream.getTracks()) track.stop();
}

/**
 * Raises the track to its capability maximum, which `getUserMedia`'s `ideal` sizes can
 * settle below.
 */
export async function maximizeResolution(track: MediaStreamTrack | undefined): Promise<void> {
  // Firefox before 132 has no getCapabilities.
  if (!track || !('getCapabilities' in track)) return;
  const { width, height } = track.getCapabilities();
  if (!width?.max || !height?.max || (track.getSettings().width ?? 0) >= width.max) return;
  try {
    await track.applyConstraints({ width: { exact: width.max }, height: { exact: height.max } });
  } catch {
    // The camera stays at the size it opened at.
  }
}

type ImageCaptureCtor = new (track: MediaStreamTrack) => { takePhoto(): Promise<Blob> };

function imageCaptureCtor(): ImageCaptureCtor | undefined {
  return (window as { ImageCapture?: ImageCaptureCtor }).ImageCapture;
}

async function grabFrame(stream: MediaStream, video: HTMLVideoElement): Promise<Blob> {
  // ImageCapture (Chromium only) returns a full-resolution still; the preview frame is
  // the fallback everywhere else.
  const ImageCapture = imageCaptureCtor();
  const track = stream.getVideoTracks()[0];
  if (ImageCapture && track) {
    try {
      return await new ImageCapture(track).takePhoto();
    } catch {
      // Some cameras reject stills; the preview frame still works.
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d')?.drawImage(video, 0, 0);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not take photo'))),
      'image/jpeg',
      0.92,
    ),
  );
}

function cameraError(e: unknown): string {
  const name = e instanceof DOMException ? e.name : '';
  if (name === 'NotAllowedError')
    return 'Camera permission denied. Choose from the gallery instead.';
  if (name === 'NotFoundError' || name === 'OverconstrainedError')
    return 'No camera found. Choose from the gallery instead.';
  return 'Camera unavailable. Choose from the gallery instead.';
}
